// Fact and relation resolvers — the enumerated, typed readers the condition
// grammar references (see RULES-ENGINE-DESIGN.md). Facts resolve against the
// *effective* view (canonical state merged with the previous pass's overlay)
// so chained rules see forced picks / auto-attached content. An unknown fact
// or relation raises RuleConditionError, which the evaluator records as a
// structured authoring error (the rule simply doesn't fire) — never a crash.
//
// Relations power the `exists` quantifier. Transitivity lives inside the
// relation: `slots` is one hop (an instance's direct slot children) while
// `assembly` is the transitive closure over slot containment in both
// directions (parent chain + all descendants — the whole physical chain).
// `connected` (live Unity hook neighbours) has no data source yet: the
// resolver exists but returns [] with a trace note, gated on the Unity
// connection event.

import type {
  ConfigInstance,
  ConfigurationState,
  EvalErrorKind,
  FactValue,
  Relation,
  RuleCatalog,
} from "./types";

/** Structured condition-evaluation failure. Carries the error kind so the
 *  evaluator can record it verbatim in EvalResult.errors. */
export class RuleConditionError extends Error {
  readonly kind: Extract<
    EvalErrorKind,
    "unknownFact" | "unknownOp" | "unknownRelation" | "missingInstance"
  >;

  constructor(kind: RuleConditionError["kind"], message: string) {
    super(message);
    this.name = "RuleConditionError";
    this.kind = kind;
  }
}

/** The effective state plus an id index — what facts and relations read. */
export interface EffectiveView {
  state: ConfigurationState;
  byId: Map<string, ConfigInstance>;
}

export function buildView(state: ConfigurationState): EffectiveView {
  const byId = new Map<string, ConfigInstance>();
  for (const inst of state.instances) byId.set(inst.instanceId, inst);
  return { state, byId };
}

/** Parse a fact expression like `materialPick[top]` or `formValue[f1][email]`
 *  into name + bracket args. */
function parseFactExpr(expr: string): { name: string; args: string[] } {
  const match = /^([A-Za-z_][A-Za-z0-9_.]*)((?:\[[^\]]*\])*)$/.exec(expr);
  if (!match) {
    throw new RuleConditionError("unknownFact", `Malformed fact expression "${expr}"`);
  }
  const args: string[] = [];
  const argPattern = /\[([^\]]*)\]/g;
  let m: RegExpExecArray | null;
  while ((m = argPattern.exec(match[2])) !== null) args.push(m[1]);
  return { name: match[1], args };
}

function requireInstance(
  instance: ConfigInstance | null,
  factName: string,
): ConfigInstance {
  if (!instance) {
    throw new RuleConditionError(
      "missingInstance",
      `Fact "${factName}" is instance-scoped but was evaluated without a target instance (scene-scoped rule?)`,
    );
  }
  return instance;
}

function requireArg(args: string[], index: number, factName: string): string {
  const arg = args[index];
  if (arg === undefined || arg === "") {
    throw new RuleConditionError(
      "unknownFact",
      `Fact "${factName}" requires argument #${index + 1} (e.g. ${factName}[key])`,
    );
  }
  return arg;
}

/** Resolve one fact against the effective view. Instance-scoped facts need a
 *  target instance; context/scene facts ignore it. Missing values resolve to
 *  null; only unknown/misused facts raise. */
export function resolveFact(
  view: EffectiveView,
  catalog: RuleCatalog,
  expr: string,
  instance: ConfigInstance | null,
): FactValue {
  const { name, args } = parseFactExpr(expr);
  switch (name) {
    case "productId":
      return requireInstance(instance, name).productId;
    case "variantKey":
      // undefined → "main", per the fact table.
      return requireInstance(instance, name).variantKey ?? "main";
    case "groupId":
      return requireInstance(instance, name).groupId ?? null;
    case "materialPick": {
      const inst = requireInstance(instance, name);
      const surfaceKey = requireArg(args, 0, name);
      const pick = inst.materialPicks[surfaceKey];
      if (pick !== undefined) return pick;
      // Fall back to the surface's authored default, like the picker does.
      const surface = catalog
        .getProduct(inst.productId)
        ?.surfaces.find((s) => s.surfaceKey === surfaceKey);
      return surface?.defaultMaterialId ?? null;
    }
    case "parameterValue": {
      const inst = requireInstance(instance, name);
      const key = requireArg(args, 0, name);
      const value = catalog.getProduct(inst.productId)?.parameterValues[key];
      return value === undefined ? null : value;
    }
    case "slotContents": {
      const inst = requireInstance(instance, name);
      const hookKey = requireArg(args, 0, name);
      return inst.slotContents[hookKey]?.productId ?? null;
    }
    case "hasConnector": {
      const inst = requireInstance(instance, name);
      const key = requireArg(args, 0, name);
      return (catalog.getProduct(inst.productId)?.connectorKeys ?? []).includes(key);
    }
    case "context.designerId":
      return view.state.context.designerId ?? null;
    case "context.market":
      return view.state.context.market ?? null;
    case "context.dealerId":
      return view.state.context.dealerId ?? null;
    case "context.lang":
      return view.state.context.lang ?? null;
    case "treeChoice":
      return view.state.context.treeChoices?.[requireArg(args, 0, name)] ?? null;
    case "formValue": {
      const formId = requireArg(args, 0, name);
      const field = requireArg(args, 1, name);
      return view.state.formValues[formId]?.[field] ?? null;
    }
    case "collectionPick":
      return view.state.collectionPicks[requireArg(args, 0, name)] ?? null;
    case "sceneContains": {
      const productId = requireArg(args, 0, name);
      return view.state.instances.some((i) => i.productId === productId);
    }
    default:
      throw new RuleConditionError(
        "unknownFact",
        `Unknown fact "${expr}" — facts are enumerated; new facts are deliberate code changes`,
      );
  }
}

/** Resolve a slot-content reference to its instance. Falls back to a minimal
 *  synthetic node when the reference is dangling (caller didn't include the
 *  child in instances[]) so productId conditions still evaluate. */
function resolveChild(
  view: EffectiveView,
  content: { productId: string; instanceId: string },
): ConfigInstance {
  return (
    view.byId.get(content.instanceId) ?? {
      instanceId: content.instanceId,
      productId: content.productId,
      materialPicks: {},
      slotContents: {},
    }
  );
}

/** Resolve the set of instances related to `instance` via `relation` (self
 *  excluded). `notes` collects trace-worthy remarks (e.g. the `connected`
 *  Unity gap). */
export function resolveRelation(
  view: EffectiveView,
  relation: Relation,
  instance: ConfigInstance | null,
  notes: string[],
): ConfigInstance[] {
  switch (relation) {
    case "scene":
      return view.state.instances.filter((i) => i !== instance);
    case "connected":
      // Live hook connectivity is Unity-internal today; the outbound
      // connection event this relation needs does not exist yet.
      notes.push(
        'relation "connected" resolved to [] — requires the Unity connection event (not yet available); use "slots"/"assembly" in the slot configurator',
      );
      return [];
    case "slots": {
      const inst = requireRelationInstance(instance, relation);
      const out: ConfigInstance[] = [];
      for (const content of Object.values(inst.slotContents)) {
        if (content) out.push(resolveChild(view, content));
      }
      return out;
    }
    case "assembly": {
      // Transitive closure over slot containment, both directions: the whole
      // physical chain (drawer → shelf → rail), parents and descendants.
      const inst = requireRelationInstance(instance, relation);
      const visited = new Set<string>([inst.instanceId]);
      const out: ConfigInstance[] = [];
      const queue: ConfigInstance[] = [inst];
      while (queue.length > 0) {
        const current = queue.shift()!;
        const neighbours: ConfigInstance[] = [];
        for (const content of Object.values(current.slotContents)) {
          if (content) neighbours.push(resolveChild(view, content));
        }
        for (const other of view.state.instances) {
          if (other.instanceId === current.instanceId) continue;
          const isParent = Object.values(other.slotContents).some(
            (c) => c?.instanceId === current.instanceId,
          );
          if (isParent) neighbours.push(other);
        }
        for (const n of neighbours) {
          if (visited.has(n.instanceId)) continue;
          visited.add(n.instanceId);
          out.push(n);
          queue.push(n);
        }
      }
      return out;
    }
    case "group": {
      const inst = requireRelationInstance(instance, relation);
      if (!inst.groupId) return [];
      return view.state.instances.filter(
        (i) => i !== inst && i.groupId === inst.groupId,
      );
    }
    default:
      throw new RuleConditionError(
        "unknownRelation",
        `Unknown relation "${relation as string}" — relations are enumerated (slots | connected | assembly | group | scene)`,
      );
  }
}

function requireRelationInstance(
  instance: ConfigInstance | null,
  relation: Relation,
): ConfigInstance {
  if (!instance) {
    throw new RuleConditionError(
      "missingInstance",
      `Relation "${relation}" is instance-scoped but was evaluated without a target instance`,
    );
  }
  return instance;
}
